import { $, $$, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { findSelectMenu } from '../lib/select-menu.js';
import { toastError } from '../lib/toast.js';

interface AutoModData {
  settings: {
    honeypot: { action: string };
    nickname: { transform: string; trigger: string };
  };
  ruleTypes: string[];
  guildId: string;
}

/**
 * AutoMod settings.
 *
 * The eleven rules share one shape, so they are read in a loop from
 * `ruleTypes` rather than field by field: a rule added to the registry is
 * rendered by the view and saved by this loop without either being touched.
 */
export function initAutoModPage(): void {
  const found = $<HTMLFormElement>('[data-automod-form]');
  if (!found) return;

  const form: HTMLFormElement = found;
  const guildId = form.dataset.guildId ?? '';

  trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: async () => {
      const types = pageData<AutoModData>()?.ruleTypes ?? [];

      const answer = await api.put<AutoModAnswer>(`/api/automod?guildId=${guildId}`, {
        guildId,

        /*
         * Only the rules that are on are sent.
         *
         * Storing eleven disabled rules per server would fill the collection
         * with settings nobody chose, and the page rebuilds a missing rule from
         * its defaults anyway.
         */
        rules: types
          .filter((type) => checkbox(form, `rule.${type}.enabled`))
          .map((type) => ({
            type,
            enabled: true,
            actions: checkedValues(form, `rule.${type}.actions`),
            durationMs: duration(text(form, `rule.${type}.duration`)),
            warnMessage: text(form, `rule.${type}.warnMessage`),
            exemptRoleIds: findSelectMenu(form, `rule.${type}.exemptRoleIds`)?.value ?? [],
            exemptChannelIds: findSelectMenu(form, `rule.${type}.exemptChannelIds`)?.value ?? [],
            threshold: number(form, `rule.${type}.threshold`, 5),
            windowSeconds: number(form, `rule.${type}.windowSeconds`, 5),
            terms: lines(form, `rule.${type}.terms`),
            allowlist: lines(form, `rule.${type}.allowlist`),
          }))
          // A rule with no action would be a setting that does nothing; the
          // switch above is how a server turns one off.
          .filter((rule) => rule.actions.length > 0),

        autoPurge: {
          enabled: checkbox(form, 'autoPurge.enabled'),
          channelIds: findSelectMenu(form, 'autoPurge.channelIds')?.value ?? [],
          olderThanHours: Number(text(form, 'autoPurge.olderThanHours')) || 24,
          botsOnly: checkbox(form, 'autoPurge.botsOnly'),
          keepAttachments: checkbox(form, 'autoPurge.keepAttachments'),
          keepPinned: checkbox(form, 'autoPurge.keepPinned'),
        },

        honeypot: {
          enabled: checkbox(form, 'honeypot.enabled'),
          channelId: findSelectMenu(form, 'honeypot.channelId')?.single ?? '',
          action: radio(form, 'honeypot.action', 'ban'),
          durationMs: duration(text(form, 'honeypot.duration')),
          exemptBots: checkbox(form, 'honeypot.exemptBots'),
          exemptRoleIds: findSelectMenu(form, 'honeypot.exemptRoleIds')?.value ?? [],
          notice: text(form, 'honeypot.notice'),
        },

        /*
         * The rules driven at Discord's end.
         *
         * `ruleId` is deliberately absent: the browser has no business carrying
         * Discord's identifier, and the server already holds it. Sending it back
         * would let a stale page overwrite a newer pointer — and the server
         * falls back to what it stored, which `nativePlan` is written for.
         */
        native: $$<HTMLElement>('[data-native-rule]', form).map((row) => ({
          kind: row.dataset.nativeRule ?? '',
          enabled: row.querySelector<HTMLInputElement>('[data-field="enabled"]')?.checked ?? false,
          actions: findSelectMenu(row, 'actions')?.value ?? [],
          alertChannelId: findSelectMenu(row, 'alertChannelId')?.single ?? '',
          customMessage: rowText(row, 'customMessage'),
          // Same grammar as every other duration here (`10m`, `1h`, `7d`): a
          // server that has learned to type `7d` in one field should not find
          // it means something else in another.
          timeoutSeconds: Math.round(duration(rowText(row, 'timeoutSeconds')) / 1000),
          keywords: rowLines(row, 'keywords'),
          regexPatterns: rowLines(row, 'regexPatterns'),
          presets: findSelectMenu(row, 'presets')?.value ?? [],
          mentionTotal: Number(rowText(row, 'mentionTotal')) || 5,
        })),

        nickname: {
          enabled: checkbox(form, 'nickname.enabled'),
          template: text(form, 'nickname.template') || '{username}',
          transform: radio(form, 'nickname.transform', 'none'),
          trigger: radio(form, 'nickname.trigger', 'join'),
          roleId: findSelectMenu(form, 'nickname.roleId')?.single ?? '',
        },
      });

      /*
       * Reported after the save, never instead of it.
       *
       * A native rule that only alerts leaves the message published, so the bot
       * filter fires on it too and the member is punished twice. Some servers
       * want exactly that — the native alert for the log, the bot sanction for
       * the member — so this warns and changes nothing. Switching a filter off
       * for them would be the silent rewrite this repository removed from the
       * logging module.
       */
      for (const overlap of answer.overlaps ?? []) {
        toastError(
          ct(
            'automodOverlap',
            'Saved. {kind} only alerts, so the bot filter “{filter}” will act on the same message.',
            { kind: overlap.kind, filter: overlap.filter },
          ),
        );
      }

      // A rule Discord refused: the edit is stored, but nothing was pushed.
      for (const kind of answer.refused ?? []) {
        toastError(ct('automodRefused', 'Discord refused the rule “{kind}”.', { kind }));
      }
    },
  });
}

interface AutoModAnswer {
  overlaps?: { kind: string; filter: string }[];
  refused?: string[];
}

/** A text field read relative to its row, never by a form-wide name. */
function rowText(row: ParentNode, name: string): string {
  const node = row.querySelector(`[data-field="${CSS.escape(name)}"]`);
  return node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement
    ? node.value.trim()
    : '';
}

/** One entry per line, blanks dropped: the shape every list field here uses. */
function rowLines(row: ParentNode, name: string): string[] {
  return rowText(row, name)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

/** Values of the ticked boxes sharing a name. */
function checkedValues(form: HTMLFormElement, name: string): string[] {
  return $$<HTMLInputElement>(`input[name="${CSS.escape(name)}"]`, form)
    .filter((input) => input.checked)
    .map((input) => input.value);
}

function radio(form: HTMLFormElement, name: string, fallback: string): string {
  const field = form.elements.namedItem(name);
  if (field instanceof RadioNodeList) return field.value || fallback;
  return field instanceof HTMLInputElement ? field.value || fallback : fallback;
}

function checkbox(form: HTMLFormElement, name: string): boolean {
  const field = form.elements.namedItem(name);
  if (field instanceof RadioNodeList) {
    return [...field].some((node) => node instanceof HTMLInputElement && node.checked);
  }
  return field instanceof HTMLInputElement ? field.checked : false;
}

function text(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
    ? field.value.trim()
    : '';
}

function number(form: HTMLFormElement, name: string, fallback: number): number {
  const field = form.elements.namedItem(name);
  const value = field instanceof HTMLInputElement ? Number(field.value) : fallback;
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function lines(form: HTMLFormElement, name: string): string[] {
  const field = form.elements.namedItem(name);
  if (!(field instanceof HTMLTextAreaElement)) return [];

  return field.value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

/** Reads `2h30m` into milliseconds; a bare number is minutes. */
function duration(input: string): number {
  const units: Record<string, number> = {
    s: 1000,
    m: 60_000,
    h: 3_600_000,
    d: 86_400_000,
    w: 604_800_000,
  };
  let total = 0;

  for (const match of input.toLowerCase().matchAll(/(\d+)\s*([smhdw])/g)) {
    total += Number(match[1]) * (units[match[2] ?? ''] ?? 0);
  }

  if (total === 0 && /^\d+$/.test(input)) total = Number(input) * 60_000;
  return total;
}
